import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useModelListBasedOnUserType} from '../../../../store/model';
import {ModelDTO} from 'hemwb-api';
import DefaultPage from '../../../layout/common/DefaultPage';
import styles from './ModelList.module.scss';
import {modelListGridId, useStoredGridState} from '../../../../store/grid';
import {filterModels, ModelFilter, ModelFilterState} from '../filter';
import {isExternalUser, isAdmin} from '../../../../permissions/usePermissions';
import {useAuth} from '../../../../store/auth';
import {Table, Flex, Drawer, Typography, Button} from 'antd';
import {DownOutlined, RightOutlined, DownloadOutlined} from '@ant-design/icons';
import {filterFavoritesHierarchy, nestModels, searchModels, searchMyModels} from './utils';
import {useGetModelListColumns} from './useGetModelListColumns';
import {ModelListTopButtons} from './ModelListTopButtons';
import {useNavigate} from 'react-router-dom';
import {getUrlRoute, MRAppRoutes} from '../../../router/routes';
import {useSelector} from '../../../../store/rootReducer';
import {modelListSelector} from '../../../../store/model/selectors';
import {AsyncActionPhase} from '../../../../store/asyncUtilsTypes';
import ModelDownloadAllSideBarModalAntd from '../common/ModelDownloadAllSideBarModal/ModelDownloadAllSideBarModalAntd';
import SideBarModal from '../../../layout/common/SideBarModal';

const ModelList: React.FC = () => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean>(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const tableRef = useRef<any>(null);
  const scrollRestoreAttempted = useRef(false);
  const adminUser = isAdmin();

  const externalUser = isExternalUser();
  const isExternalUserFlag = externalUser !== null ? externalUser : false;

  // Use only Redux state for expanded rows
  const gridState = useStoredGridState<ModelFilterState>(modelListGridId);
  const columns = useGetModelListColumns(isExternalUserFlag);

  // Track multiple expanded rows for hierarchical tree - persist to sessionStorage
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>(() => {
    const saved = window.sessionStorage.getItem('modelListExpandedRowKeys');
    return saved ? JSON.parse(saved) : [];
  });

  // Track if parent has been clicked once (expanded children) - second click will navigate
  const [clickedOnce, setClickedOnce] = useState<Record<string, boolean>>({});

  // Track selected row ID - NOT persisted to sessionStorage on refresh
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  // Save the clicked row ID before navigating and mark it as selected
  const saveClickedRowId = useCallback((rowId: string) => {
    window.sessionStorage.setItem('modelListClickedRowId', rowId);
    setSelectedRowId(rowId);
  }, []);

  const list = useModelListBasedOnUserType(externalUser);
  const nestedList = useMemo(() => nestModels(list ?? []), [list]);
  const {auth} = useAuth();
  const user = auth.loggedInUser;

  // Get loading state from Redux
  const modelListState = useSelector(modelListSelector);
  const isLoading = modelListState?.state === AsyncActionPhase.START;

  // Persist expanded row keys to sessionStorage
  useEffect(() => {
    window.sessionStorage.setItem('modelListExpandedRowKeys', JSON.stringify(expandedRowKeys));
  }, [expandedRowKeys]);

  // Reset scroll restore flag when component mounts to allow highlighting on return
  useEffect(() => {
    // Only reset if we have a saved row ID (meaning we're returning from detail page)
    const savedRowId = window.sessionStorage.getItem('modelListClickedRowId');
    if (savedRowId) {
      scrollRestoreAttempted.current = false;
    }
  }, []); // Empty dependency array - only run on mount

  const filteredList = useMemo(() => {
    let result;
    if (gridState.viewMyModels && user) {
      result = nestedList && searchMyModels(filterModels(nestedList, gridState.filter), user.id, gridState.search);
    } else {
      result = nestedList && searchModels(filterModels(nestedList, gridState.filter), gridState.search);
    }
    if (gridState.viewFavorites && result) {
      result = filterFavoritesHierarchy(result);
    }
    return result;
  }, [gridState.viewMyModels, gridState.viewFavorites, user, nestedList, gridState.filter, gridState.search]);

  const handleFilterPanel = useCallback(() => {
    setIsFilterPanelOpen((prev) => !prev);
  }, []);

  const handleDownloadModalOpen = useCallback(() => {
    setIsDownloadModalOpen(true);
  }, []);

  const handleDownloadModalClose = useCallback(() => {
    setIsDownloadModalOpen(false);
  }, []);

  const numberOfGlobalItem = useMemo(
    () => filteredList?.filter(({metadata: {global}}) => global === 'true')?.length || 0,
    [filteredList],
  );

  const numberOfAdaptationItem = useMemo(
    () =>
      filteredList?.filter(({metadata: {global, country}}) => global !== 'true' && country?.length > 0)?.length || 0,
    [filteredList],
  );

  const numberOfFilters = useMemo(
    () => Object.values(gridState?.filter || {}).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0),
    [gridState.filter],
  );

  // Enhance columns with current sort state from Redux
  const columnsWithSort = useMemo(() => {
    return columns.map((col: any) => {
      if (col.key === gridState.orderBy) {
        return {
          ...col,
          sortOrder: gridState.order === 'asc' ? 'ascend' : gridState.order === 'desc' ? 'descend' : null,
        };
      }
      return col;
    });
  }, [columns, gridState.orderBy, gridState.order]);

  // Helper function to find all ancestor UUIDs of a model based on displayed hierarchy
  const findAncestors = useCallback(
    (modelUuid: string): string[] => {
      if (!filteredList) return [];

      const ancestors: string[] = [];

      // Recursively search through the nested structure to find the model and its ancestors
      const findInTree = (nodes: any[], parentChain: string[]): boolean => {
        for (const node of nodes) {
          if (node.uuid === modelUuid) {
            // Found the target model - return its ancestor chain
            ancestors.push(...parentChain);
            return true;
          }

          // If this node has children, search recursively
          if (node.children && node.children.length > 0) {
            const found = findInTree(node.children, [...parentChain, node.uuid]);
            if (found) return true;
          }
        }
        return false;
      };

      findInTree(filteredList, []);
      return ancestors;
    },
    [filteredList],
  );

  // Scroll to the clicked row when coming back from detail page
  useEffect(() => {
    const savedRowId = window.sessionStorage.getItem('modelListClickedRowId');

    if (savedRowId && !scrollRestoreAttempted.current && filteredList && list) {
      scrollRestoreAttempted.current = true; // Mark as attempted

      // Find all ancestors and expand them to ensure child is visible
      const ancestors = findAncestors(savedRowId);
      if (ancestors.length > 0) {
        setExpandedRowKeys((prev) => {
          const newKeys = new Set([...prev, ...ancestors]);
          return Array.from(newKeys);
        });
      }

      // Reset clickedOnce state for the saved row to restart the cycle
      setClickedOnce((prev) => {
        const newState = {...prev};
        delete newState[savedRowId];
        return newState;
      });

      // Set this row as selected
      setSelectedRowId(savedRowId);

      // Use a longer delay to ensure the DOM is fully rendered and rows are expanded
      setTimeout(() => {
        // Find the row element by data-row-key attribute
        const rowElement = document.querySelector(`[data-row-key="${savedRowId}"]`);

        if (rowElement) {
          // Scroll the row into view
          rowElement.scrollIntoView({behavior: 'smooth', block: 'center'});
        }

        // Clear the saved row ID after restoring
        window.sessionStorage.removeItem('modelListClickedRowId');
      }, 300); // Increased delay to allow expansion animation to complete
    }
  }, [filteredList, list, findAncestors]); // Depend on filteredList and list to ensure data is loaded

  // Handler for expand button - just toggles expand/collapse
  const handleExpandButtonClick = useCallback(
    (recordKey: string, record: any, event?: React.MouseEvent) => {
      if (event) {
        event.stopPropagation();
      }

      const isChild = !!record?.isChild;
      const isExpanded = expandedRowKeys.includes(recordKey);

      // Set this row as selected when expand button is clicked
      setSelectedRowId(recordKey);

      setExpandedRowKeys((prev) => {
        if (isExpanded) {
          // Collapse this row and all its descendants
          return prev.filter((key) => key !== recordKey);
        } else {
          // If this is a top-level parent (not a child), close all other top-level parents
          if (!isChild) {
            // Get all top-level parent uuids from filteredList
            const topLevelParentUuids = filteredList?.filter((m) => !m.isChild).map((m) => m.uuid) || [];
            // Remove all other top-level parents from expanded keys
            const withoutOtherParents = prev.filter((key) => !topLevelParentUuids.includes(key) || key === recordKey);
            return [...withoutOtherParents, recordKey];
          } else {
            // For child rows, just add to expanded keys
            return [...prev, recordKey];
          }
        }
      });
    },
    [expandedRowKeys, filteredList],
  );

  // Multi-click behavior for row clicks: expand → navigate → collapse (cycle)
  const handleToggleExpandRow = useCallback(
    (recordKey: string, hasChildren: boolean, record: any, event?: React.MouseEvent) => {
      if (event) {
        event.stopPropagation();
      }

      const isCurrentlyExpanded = expandedRowKeys.includes(recordKey);
      const isChild = !!record?.isChild;

      if (hasChildren) {
        // Multi-click behavior for rows with children
        if (!isCurrentlyExpanded) {
          // First click: expand to show children and select this row
          setSelectedRowId(recordKey);
          setExpandedRowKeys((prev) => {
            // If this is a top-level parent, close all other top-level parents
            if (!isChild) {
              // Get all top-level parent uuids from filteredList
              const topLevelParentUuids = filteredList?.filter((m) => !m.isChild).map((m) => m.uuid) || [];
              // Remove all other top-level parents from expanded keys
              const withoutOtherParents = prev.filter((key) => !topLevelParentUuids.includes(key) || key === recordKey);
              return [...withoutOtherParents, recordKey];
            } else {
              return [...prev, recordKey];
            }
          });
          setClickedOnce((prev) => ({...prev, [recordKey]: true}));
        } else if (clickedOnce[recordKey]) {
          // Second click: navigate to detail page
          saveClickedRowId(recordKey);
          navigate(getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid: recordKey}));
        } else {
          // Third click (after coming back): collapse and reset
          setExpandedRowKeys((prev) => prev.filter((key) => key !== recordKey));
          setClickedOnce((prev) => {
            const newState = {...prev};
            delete newState[recordKey];
            return newState;
          });
        }
      } else {
        // No children: navigate immediately
        // If this is a top-level row, collapse all other expanded rows
        if (!isChild) {
          setExpandedRowKeys([]);
          setClickedOnce({});
        }
        saveClickedRowId(recordKey);
        navigate(getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid: recordKey}));
      }
    },
    [expandedRowKeys, clickedOnce, navigate, saveClickedRowId, filteredList],
  );

  const resetExpandedState = useCallback(() => {
    setClickedOnce({});
    setExpandedRowKeys([]);
    setSelectedRowId(null);
  }, []);

  const handleFilterChange = useCallback(
    (filterState: ModelFilterState) => {
      if (gridState.page !== 0) {
        gridState.setPage(0);
      }
      gridState.setFilter(filterState);
      resetExpandedState();
    },
    [gridState, resetExpandedState],
  );

  const handleRowClick = useCallback(
    (record: ModelDTO & {children?: ModelDTO[]; isChild?: boolean}) => {
      return {
        onClick: (event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (
            target.closest('button') ||
            target.closest('a') ||
            target.closest('.ant-table-cell-expand-icon-cell') ||
            target.closest('[role="button"]') ||
            target.closest('.ant-modal-root') ||
            target.closest('.ant-modal-wrap') ||
            target.closest('.ant-modal-mask') ||
            target.closest('input[type="checkbox"]') ||
            target.closest('.ant-checkbox') ||
            target.closest('[type="checkbox"]')
          ) {
            return;
          }

          const hasChildren = !!record.children?.length;

          // Set this row as selected on any click
          setSelectedRowId(record.uuid);

          // If this is a top-level row without children, collapse all other expanded rows
          if (!record.isChild && !hasChildren) {
            setExpandedRowKeys([]);
            setClickedOnce({});
          }

          // Child rows: if they have children, allow expand behavior; otherwise navigate
          if (record.isChild && !hasChildren) {
            saveClickedRowId(record.uuid);
            navigate(getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid: record.uuid}));
            return;
          }

          // All rows with children use the expand/navigate cycle
          handleToggleExpandRow(record.uuid, hasChildren, record, event);
        },
      };
    },
    [handleToggleExpandRow, navigate, saveClickedRowId],
  );

  const paginationConfig = useMemo(
    () => ({
      current: (gridState.page || 0) + 1,
      pageSize: gridState.rowsPerPage || 20,
      total: filteredList?.length || 0,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} models`,
      pageSizeOptions: [10, 20, 50, 100],
      onChange: (page: number, size: number) => {
        gridState.setPage(page - 1);
        gridState.setRowsPerPage?.(size);
        resetExpandedState();
      },
      onShowSizeChange: (_current: number, size: number) => {
        gridState.setPage(0);
        gridState.setRowsPerPage?.(size);
        resetExpandedState();
      },
    }),
    [gridState, filteredList?.length, resetExpandedState],
  );

  return (
    <DefaultPage style={{backgroundColor: '#FFFFFF', margin: '26px 20px 0', overflow: 'hidden'}}>
      {isExternalUserFlag ? null : (
        <Drawer title="More" open={isFilterPanelOpen} onClose={handleFilterPanel} styles={{body: {padding: 0}}}>
          <Flex
            align="center"
            style={{
              width: '100%',
              height: '48px',
              backgroundColor: '#F5F5F5',
              padding: '0 24px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            }}
          >
            <Typography.Text style={{fontWeight: 700}}>Applied Filters ({numberOfFilters})</Typography.Text>
          </Flex>
          <ModelFilter models={list} filter={gridState.filter} onFilterChange={handleFilterChange} />
        </Drawer>
      )}
      <ModelListTopButtons
        isExternalUser={isExternalUserFlag}
        handleFilterPanel={handleFilterPanel}
        numberOfFilters={numberOfFilters}
      />

      <Flex
        justify={gridState?.search ? 'space-between' : 'flex-end'}
        align="center"
        gap="16px"
        style={{marginBottom: '32px'}}
      >
        {gridState?.search && (
          <p style={{fontSize: '12px', margin: 0}}>
            We found {numberOfGlobalItem} <strong>Global Models</strong> and {numberOfAdaptationItem}{' '}
            <strong>Adaptations</strong>
          </p>
        )}
        {!adminUser ? null : (
          <Button
            onClick={handleDownloadModalOpen}
            type="default"
            icon={<DownloadOutlined />}
            style={{fontWeight: 'bold', flexShrink: 0}}
          >
            Download all entries
          </Button>
        )}
      </Flex>

      <div className={styles.tableWrapper}>
        <Table<ModelDTO>
          ref={tableRef}
          columns={columnsWithSort}
          rowKey={(record) => record.uuid}
          rowClassName={(record: ModelDTO & {isChild?: boolean; children?: ModelDTO[]}) => {
            // If this row is selected, apply selected class
            if (selectedRowId === record.uuid) {
              return record.isChild ? `${styles.childRow} ${styles.selectedRow}` : styles.selectedRow;
            }
            // Otherwise apply normal classes
            if (record.isChild) return styles.childRow;
            if (expandedRowKeys.includes(record.uuid)) return styles.expandedParentRow;
            return '';
          }}
          dataSource={filteredList ?? []}
          loading={isLoading}
          pagination={paginationConfig}
          onChange={(pagination, filters, sorter: any) => {
            if (sorter && !Array.isArray(sorter)) {
              gridState.setOrderBy(sorter.columnKey || null);
              gridState.setOrder(sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : 'asc');
            }
          }}
          expandable={{
            expandRowByClick: false,
            defaultExpandAllRows: false,
            rowExpandable: (model: any) => !!model?.children?.length,
            expandIconColumnIndex: 1,
            expandedRowKeys: expandedRowKeys,
            expandIcon: ({record: model, expanded}) => {
              const hasChildren = !!model?.children?.length;
              const isChild = !!(model as any)?.isChild;
              const level = (model as any)?.level || 0;

              // Show expand icon for any row with children (parent or nested child)
              if (!hasChildren) {
                return null;
              }

              // Calculate left margin for nested expand icons
              // Level 0: 0px, Level 1: 16px, Level 2: 48px, etc.
              const leftMargin = (level + 1) * 16;

              return (
                <button
                  className={`ant-table-row-expand-icon customExpandIcon ${
                    expanded ? 'ant-table-row-expand-icon-expanded' : 'ant-table-row-expand-icon-collapsed'
                  }`}
                  onClick={(e) => {
                    handleExpandButtonClick(model.uuid, model, e);
                  }}
                  aria-label={expanded ? 'Collapse row' : 'Expand row'}
                  style={{
                    //position: isChild ? 'relative' : 'absolute',
                    //top: isChild ? 'auto' : '45%',
                    //transform: isChild ? 'none' : 'translate(50%, -50%)',
                    marginLeft: isChild ? `${leftMargin}px` : undefined,
                    marginRight: isChild ? 16 : undefined,
                  }}
                >
                  {expanded ? (
                    <DownOutlined style={{fontSize: 16, color: '#006A63'}} />
                  ) : (
                    <RightOutlined style={{fontSize: 16, color: '#000000'}} />
                  )}
                </button>
              );
            },
          }}
          onRow={handleRowClick}
        />
      </div>
      <SideBarModal isOpen={isDownloadModalOpen} closeModal={handleDownloadModalClose}>
        <ModelDownloadAllSideBarModalAntd
          isOpen={isDownloadModalOpen}
          closeModal={handleDownloadModalClose}
          filterState={gridState.filter ?? undefined}
          searchValue={gridState.search}
        />
      </SideBarModal>
    </DefaultPage>
  );
};

export default ModelList;
